import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, X, Calendar as CalendarIcon, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { ActionCategory, SensitivityLevel } from '@/types/compliance';
import { actionCategoryLabels, sensitivityLabels } from '@/types/compliance';

export interface AuditFiltersState {
  search: string;
  entityType: string;
  actionCategory: ActionCategory | 'all';
  sensitivityLevel: SensitivityLevel | 'all';
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

interface AuditFiltersProps {
  filters: AuditFiltersState;
  onChange: (filters: AuditFiltersState) => void;
  entityTypes?: string[];
}

const defaultFilters: AuditFiltersState = {
  search: '',
  entityType: 'all',
  actionCategory: 'all',
  sensitivityLevel: 'all',
  dateFrom: undefined,
  dateTo: undefined,
};

export function AuditFilters({ filters, onChange, entityTypes = [] }: AuditFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const activeFiltersCount = [
    filters.entityType !== 'all',
    filters.actionCategory !== 'all',
    filters.sensitivityLevel !== 'all',
    filters.dateFrom !== undefined,
    filters.dateTo !== undefined,
  ].filter(Boolean).length;

  const handleReset = () => {
    onChange(defaultFilters);
  };

  const updateFilter = <K extends keyof AuditFiltersState>(
    key: K,
    value: AuditFiltersState[K]
  ) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar en registros de auditoria..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-9"
          />
        </div>

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filtros
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filtros Avanzados</h4>
                <Button variant="ghost" size="sm" onClick={handleReset}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Limpiar
                </Button>
              </div>

              {/* Entity Type */}
              <div className="space-y-2">
                <Label>Tipo de Entidad</Label>
                <Select
                  value={filters.entityType}
                  onValueChange={(v) => updateFilter('entityType', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las entidades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las entidades</SelectItem>
                    {entityTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Category */}
              <div className="space-y-2">
                <Label>Categoria de Accion</Label>
                <Select
                  value={filters.actionCategory}
                  onValueChange={(v) => updateFilter('actionCategory', v as ActionCategory | 'all')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las categorias" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorias</SelectItem>
                    {Object.entries(actionCategoryLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sensitivity Level */}
              <div className="space-y-2">
                <Label>Nivel de Sensibilidad</Label>
                <Select
                  value={filters.sensitivityLevel}
                  onValueChange={(v) => updateFilter('sensitivityLevel', v as SensitivityLevel | 'all')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los niveles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los niveles</SelectItem>
                    {Object.entries(sensitivityLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <Label>Rango de Fechas</Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "flex-1 justify-start text-left font-normal",
                          !filters.dateFrom && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateFrom
                          ? format(filters.dateFrom, 'dd/MM/yy', { locale: es })
                          : 'Desde'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateFrom}
                        onSelect={(date) => updateFilter('dateFrom', date)}
                        locale={es}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "flex-1 justify-start text-left font-normal",
                          !filters.dateTo && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateTo
                          ? format(filters.dateTo, 'dd/MM/yy', { locale: es })
                          : 'Hasta'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateTo}
                        onSelect={(date) => updateFilter('dateTo', date)}
                        locale={es}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Active filters badges */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.entityType !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              Entidad: {filters.entityType}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter('entityType', 'all')}
              />
            </Badge>
          )}
          {filters.actionCategory !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              Accion: {actionCategoryLabels[filters.actionCategory]}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter('actionCategory', 'all')}
              />
            </Badge>
          )}
          {filters.sensitivityLevel !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              Nivel: {sensitivityLabels[filters.sensitivityLevel]}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter('sensitivityLevel', 'all')}
              />
            </Badge>
          )}
          {filters.dateFrom && (
            <Badge variant="secondary" className="gap-1">
              Desde: {format(filters.dateFrom, 'dd/MM/yy', { locale: es })}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter('dateFrom', undefined)}
              />
            </Badge>
          )}
          {filters.dateTo && (
            <Badge variant="secondary" className="gap-1">
              Hasta: {format(filters.dateTo, 'dd/MM/yy', { locale: es })}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter('dateTo', undefined)}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
